import type { ComponentType } from 'react';
import styled, { css } from 'styled-components';
import { Globe16, Planet, Translate } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { useSettings } from '@wu/widget-core';
import type { WebsiteTranslatorSettings } from '../../types';

const onlyFlagsSquaredIconSize: Record<
  WebsiteTranslatorSettings['widgetSize'],
  [number, number]
> = {
  small: [18, 13.5],
  medium: [20, 15],
  large: [24, 18]
};

const onlyFlagsRoundedIconSize: Record<
  WebsiteTranslatorSettings['widgetSize'],
  number
> = {
  small: 18,
  medium: 20,
  large: 24
};

const squaredIconSize: Record<
  WebsiteTranslatorSettings['widgetSize'],
  [number, number]
> = {
  small: [16, 12],
  medium: [18, 13.5],
  large: [22, 16.5]
};

const roundedIconSize: Record<WebsiteTranslatorSettings['widgetSize'], number> =
  {
    small: 16,
    medium: 18,
    large: 22
  };

const onlyFlagsRoundedIconSVGSize: Record<
  WebsiteTranslatorSettings['widgetSize'],
  [number, number]
> = {
  small: [26, 18],
  medium: [28, 20],
  large: [32, 24]
};

const roundedIconSVGSize: Record<
  WebsiteTranslatorSettings['widgetSize'],
  [number, number]
> = {
  small: [24, 16],
  medium: [26, 18],
  large: [30, 22]
};

const onlyFlagsMarginValue = {
  small: 2.25,
  medium: 2.5,
  large: 3
};

const marginValue = {
  small: 2,
  medium: 2.25,
  large: 2.75
};

const iconMapping = {
  globe: Globe16,
  planet: Planet,
  translate: Translate
};

export type FlagProps = {
  icon?: ComponentType;
  iconName?: 'globe' | 'planet' | 'translate';
  style?: 'rounded' | 'squared';
  languageName?: string;
  isDecorative?: boolean;
};

export function Flag({
  icon,
  iconName,
  style,
  languageName,
  isDecorative = false
}: FlagProps) {
  const { widgetSize, style: widgetStyle, textColor } = useSettings();
  const onlyFlags = widgetStyle === 'style1' || widgetStyle === 'style2';

  const currentSquaredIconSize = onlyFlags
    ? onlyFlagsSquaredIconSize[widgetSize]
    : squaredIconSize[widgetSize];

  const currentMarginValue = onlyFlags
    ? onlyFlagsMarginValue[widgetSize]
    : marginValue[widgetSize];

  const currentRoundedIconSize = onlyFlags
    ? onlyFlagsRoundedIconSize[widgetSize]
    : roundedIconSize[widgetSize];

  const currentRoundedIconSVGSize = onlyFlags
    ? onlyFlagsRoundedIconSVGSize[widgetSize]
    : roundedIconSVGSize[widgetSize];

  if (iconName) {
    return (
      <Icon
        component={iconMapping[iconName]}
        size={roundedIconSize[widgetSize]}
        fill={textColor || undefined}
        aria-hidden={isDecorative}
        aria-label={isDecorative ? undefined : languageName}
      />
    );
  }

  return (
    <StyledIcon
      _style={style}
      _verticalMargin={currentMarginValue}
      _roundedIconSize={currentRoundedIconSize}
      component={icon}
      size={
        style === 'squared' ? currentSquaredIconSize : currentRoundedIconSVGSize
      }
      aria-hidden={isDecorative}
      aria-label={isDecorative ? undefined : languageName}
    />
  );
}

const StyledIcon = styled(Icon)<{
  _style?: 'rounded' | 'squared';
  _verticalMargin: number;
  _roundedIconSize: number;
}>`
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.7) 0%,
      rgba(0, 0, 0, 0.15) 100%
    );
    background-blend-mode: overlay;
    mix-blend-mode: multiply;
    z-index: 2;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border: 1px solid rgba(118, 118, 118, 0.1);
    border-radius: inherit;
    z-index: 2;
  }

  ${({ _style, _roundedIconSize }) =>
    _style === 'rounded' &&
    css`
      position: relative;
      width: ${_roundedIconSize}px;
      height: ${_roundedIconSize}px;
      border-radius: 50%;

      & svg {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 1;
        transform: translate(-50%, -50%);
      }
    `}

  ${({ _style, _verticalMargin }) =>
    _style === 'squared' &&
    css`
      margin: ${_verticalMargin}px 0;
      border-radius: 2px;
    `}
`;
